<template>
  <div class="boon" id="app">
    <transition name="animated" mode="out-in">
      <router-view v-if="shouldShowView" key="view" />
      <login-form v-else key="login" />
    </transition>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import LoginForm from '@/components/login/LoginForm';
import { EventBus } from '@/common/eventbus';
import { checkViewPermissions } from '@/store/actionManager';
import { vuex as store } from '@/store';

export default {
  name: 'App',

  components: {
    LoginForm,
  },

  metaInfo() {
    return {
      titleTemplate: this.titleTemplate,
    };
  },

  data() {
    return {
      titleTemplate: '',
      currentRouteName: null,
    };
  },

  computed: {
    ...mapGetters(['isLoggedIn', 'currentProject']),

    shouldShowView() {
      return this.isLoggedIn && checkViewPermissions(this.currentRouteName, store.state);
    },
  },

  async mounted() {
    var versionInfo = await fetch('static/version.json').then((response) => {
      return response.json();
    });
    console.log(
      'Running ' + versionInfo.name + '%c' + versionInfo.version,
      'padding: 2px; background: #222255; border-radius: 3px;',
      'Built with pride on ' + versionInfo.buildDate
    );

    setInterval(async () => {
      var serverVersionInfo = await fetch('static/version.json').then((response) => {
        return response.json();
      });
      if (serverVersionInfo.version !== versionInfo.version) {
        console.log('Server has', serverVersionInfo);
        EventBus.$emit('showMessageToUser', {
          message: this.$t('There is a new version of the application, please reload browser'),
          type: 'error',
          duration: 13000,
        });
      }
    }, 15000);

    this.updateBrowserTitle();
  },

  watch: {
    $route: {
      immediate: true,
      handler(next) {
        this.currentRouteName = (next && next.name) || null;
        this.updateBrowserTitle();
      },
    },
  },

  methods: {
    updateBrowserTitle() {
      this.titleTemplate =
        (this.currentProject && this.currentProject._id
          ? [this.currentProject.projectNumber, this.currentProject.title].filter(Boolean).join(' ').trim()
          : 'Boon') + ' - %s';
    },
  },
};
</script>

<style>
@import '~bootstrap/dist/css/bootstrap.css';
@import '~bootstrap-vue/dist/bootstrap-vue.min.css';
@import '~bootstrap-icons/font/bootstrap-icons.css';

/* @import url('https://rsms.me/inter/inter-ui.css'); */

@import url('https://fonts.googleapis.com/css?family=Cabin:400,400i,500,500i,600,600i,700|Montserrat:300,300i,400,400i,500,500i,600,600i,700&display=swap');

/** @todo Copy-pasted font from bootstrap-icons library. Seems like problems with webpack-dev-server. Have no time to dive into that right now. */
@font-face {
  font-display: block;
  font-family: 'bootstrap-icons';
  src: url('/static/fonts/bootstrap-icons-v1.10.5.woff2') format('woff2'),
    url('/static/fonts/bootstrap-icons-v1.10.5.woff') format('woff');
}

@font-face {
  /* font-family: 'DIN Alternate Bold'; */
  /* font-style: normal; */
  /* font-weight: normal; */
  /* src: local('DIN Alternate Bold'), url('/static/fonts/DINAlternate-Bold.woff') format('woff'); */

  font-family: 'DIN Alternate Bold';
  /* src: url('location.eot'); */
  src:
      /* url('location.eot#iefix') format('embedded-opentype'), */ url('/static/fonts/DINAlternate-Bold.woff')
      format('woff'),
    url('/static/fonts/DINAlternate-Bold.ttf') format('truetype');
  font-weight: normal;
  font-style: normal;
}
@font-face {
  font-family: 'DIN-Light';
  src: url('/static/fonts/DINLight.woff') format('woff'); /* Modern Browsers */
  font-weight: normal;
  font-style: normal;
}

@font-face {
  font-family: 'Space Grotesk';
  src: url('/static/fonts/spacegrotesk-bold-webfont.woff2') format('woff2'),
    url('/static/fonts/spacegrotesk-bold-webfont.woff') format('woff');
  font-weight: 700;
  font-style: normal;
}
@font-face {
  font-family: 'Space Grotesk';
  src: url('/static/fonts/spacegrotesk-light-webfont.woff2') format('woff2'),
    url('/static/fonts/spacegrotesk-light-webfont.woff') format('woff');
  font-weight: 300;
  font-style: normal;
}
@font-face {
  font-family: 'Space Grotesk';
  src: url('/static/fonts/spacegrotesk-medium-webfont.woff2') format('woff2'),
    url('/static/fonts/spacegrotesk-medium-webfont.woff') format('woff');
  font-weight: 500;
  font-style: normal;
}
@font-face {
  font-family: 'Space Grotesk';
  src: url('/static/fonts/spacegrotesk-regular-webfont.woff2') format('woff2'),
    url('/static/fonts/spacegrotesk-regular-webfont.woff') format('woff');
  font-weight: 400;
  font-style: normal;
}
@font-face {
  font-family: 'Space Grotesk';
  src: url('/static/fonts/spacegrotesk-semibold-webfont.woff2') format('woff2'),
    url('/static/fonts/spacegrotesk-semibold-webfont.woff') format('woff');
  font-weight: 600;
  font-style: normal;
}

*,
*:after,
*:before {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0) !important;
  -webkit-font-smoothing: subpixel-antialiased;
}

.cf:before {
  content: '';
  display: table;
  clear: both;
}

body {
  margin: 0px;
  background: #242f39;
  color: white;
  font-family: PoppinsRegular, sans-serif;
  /** @todo PoppinsRegular has font-weight 400. */
  font-weight: 200;
  font-size: 16px;
  letter-spacing: 0.5px;
  -webkit-font-smoothing: subpixel-antialiased;
}

img {
  max-width: 100%;
  vertical-align: middle;
}

button {
  background: inherit;
  border: none;
  color: white;
}

a {
  cursor: pointer;
  text-decoration: none;
  color: inherit;
}

a:visited {
  color: inherit;
}

input,
textarea,
select,
button {
  font: inherit;
  border: 0px;
}

input:read-only,
textarea:read-only {
  color: #888;
}

input:focus,
button:focus,
textarea:focus {
  outline: 0;
}

main {
  -webkit-box-flex: 1;
  flex: 1 0 auto;
  /* Header's height is 80px; subheader's height is 3.375rem */
  margin-top: calc(80px + 3.375rem);
}

.fill-width {
  width: 100%;
}

.b-icon.bi {
  /* Removes spacing below svg */
  vertical-align: middle;
}

.icon-button {
  border-radius: 50%;
  font-size: 48px;
  padding: 0px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
}

.icon-button:hover {
  color: inherit !important;
}

.cursor-pointer {
  cursor: pointer;
}

.cursor-help {
  cursor: help;
}

.negative-button {
  background: rgb(175, 55, 57);
  background: linear-gradient(149deg, rgba(223, 144, 61, 1) 0%, rgb(175, 55, 57) 95%);
}

.positive-button {
  background: rgb(36, 158, 83);
  background: linear-gradient(149deg, rgb(36, 158, 83) 0%, rgb(25, 82, 47) 95%);
}

.glyphicon-move {
  display: inline-block;
  cursor: move;
  color: #1c2731;
  transition-duration: 0.5s;
  transition-timing-function: ease-in-out;
  width: 6px;
}

.glyphicon-no-move {
  display: inline-block;
  color: #1c2731;
  width: 6px;
}

.container {
  max-width: 100vw;
  margin: 0 auto;
  position: relative;
}

.container-padding {
  padding: 0 20px;
}

h2 {
  font-size: 130%;
}

.drive-enter-active {
  transition: all 1s;
}

.drive-leave-active {
  transition: all 3s;
}

.drive-enter {
  transform: translateX(-30px);
  opacity: 0;
}

.drive-leave-to {
  transform: translateX(30px);
  opacity: 0;
}

.fade-enter-active {
  transition: opacity 0.5s;
}

.fade-leave-active {
  transition: opacity 0.1s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.slowfade-leave-active {
  transition: opacity 5s;
}

.slowfade-leave-to {
  opacity: 0;
}

.half {
  width: calc(50% - 28px);
}

.third {
  width: calc(25% - 28px);
}

.full {
  width: 100%;
}

.modalBoon {
  background: #1c2731;
  color: #fff;
  padding: 20px;
  display: flex;
  flex-direction: column;
  transition: all 0.7s cubic-bezier(0.845, -0.265, 0.19, 1.28);
  box-shadow: 1px 1px 4px rgba(0, 0, 0, 0.2), -1px 0 4px rgba(0, 0, 0, 0.2);
  text-align: center;
}

.modalBoon h2,
p {
  margin-bottom: 0.5cm;
}

.modalBoon ul.modalButtons {
  display: flex;

  list-style: none;
  margin: 0 -20px;
  padding: 0;
  position: absolute;
  width: 100%;
  bottom: 0px;
}

.modalBoon ul.modalButtons > li {
  display: inline-block;
  width: 50%;
  text-align: center;
}

.modalBoon button {
  padding: 20px !important;
  width: 100%;
  height: 100%;
  color: #fff !important;
  border: none !important;
  text-transform: uppercase;
  border-radius: 0px;
}

.modal-close-button {
  margin-left: auto;
  position: absolute;
  text-transform: capitalize;
  font-size: 1rem;
  line-height: 2rem;
  right: 0px;
  top: 0px;
  color: #666 !important;
  background: transparent;
  width: auto !important;
  height: auto !important;
  align-items: center;
}

.modal-close-button .b-icon {
  font-size: 2.5rem;
}

.modal-close-button:hover {
  color: #000 !important;
}

.modalBoon .modal-close-button {
  color: #666 !important;
}

.modalBoon .modal-close-button:hover {
  color: #000 !important;
}

.file-status-approved,
.btn-file-status-approved {
  color: #fff !important;
  background-color: #6ab291 !important;
}

.file-status-for-approval,
.btn-file-status-for-approval {
  color: var(--bs-body-color) !important;
  background-color: #f5c968 !important;
}

.file-status-not-loaded,
.btn-file-status-not-loaded {
  color: var(--bs-body-color) !important;
  background-color: #fff !important;
}

.file-status-wrong,
.btn-file-status-wrong {
  color: #fff !important;
  background-color: #f0504a !important;
}

.darkButton {
  background: #373e4d !important;
}

.darkButton[disabled] {
  background: #4a5468 !important;
  color: #939393;
}

.noWrapButton {
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  padding-left: 3px;
}

.lightButton {
  background: #4b515e !important;
}

.boon {
  display: flex;
  min-height: 100vh;
  flex-direction: column;
  overflow-x: hidden;
}

.boon .atwho-view {
  position: relative;
}

@media screen and (max-width: 900px) {
  .hidden-on-small-screens {
    display: none;
  }
}

@page {
  /* If you change here, make sure printing of projectplan does not span extra pages */
  margin: 0mm;
}

body {
  margin: 0;
}

/** Paper sizes **/
.A1 {
  width: 594mm;
  height: 840mm;
}
.A1.landscape {
  width: 841mm;
  height: 593mm;
}
.A2 {
  width: 420mm;
  height: 593mm;
}
.A2.landscape {
  width: 594mm;
  height: 419mm;
}
.A3 {
  width: 297mm;
  height: 419mm;
}
.A3.landscape {
  width: 420mm;
  height: 296mm;
}
.A4 {
  width: 210mm;
  height: 296mm;
}
.A4.landscape {
  width: 297mm;
  height: 209mm;
}
.A5 {
  width: 148mm;
  height: 209mm;
}
.A5.landscape {
  width: 210mm;
  height: 147mm;
}

/** Fix for Chrome issue #273306 **/
@media print {
  ::-webkit-scrollbar {
    display: none;
  }
  body {
    background: white !important;
    padding: 0px;
  }
  body.A1.landscape {
    width: 841mm;
  }
  body.A1,
  body.A2.landscape {
    width: 594mm;
  }
  body.A2,
  body.A3.landscape {
    width: 420mm;
  }
  body.A3,
  body.A4.landscape {
    width: 297mm;
  }
  body.A4,
  body.A5.landscape {
    width: 210mm;
  }
  body.A5 {
    width: 148mm;
  }

  main {
    padding-top: 0px;
    margin-top: 0px;
  }

  .editChild {
    display: none;
  }

  header {
    display: none;
  }

  aside {
    display: none;
  }

  .footer {
    display: none;
  }

  .hide-on-print {
    display: none !important;
  }
}

.sheet {
  padding: 5mm;
}
/***************************************************************************************
    popup menu
***************************************************************************************/

.actionMenu {
  background: #151c24;
  border: 1px solid #151c24;
  border-radius: 6px;
}

.ctx-menu {
  box-shadow: 3px 3px 6px #1c2731;
}

.ctx-menu div:hover {
  background: #dedede;
}

.ctx-menu div {
  white-space: nowrap;
  margin-left: 5px;
  margin-right: 5px;
  padding: 3px;
  border-radius: 3px;
}

/***************************************************************************************
    enter/exit anim
***************************************************************************************/
.list-enter-active,
.list-leave-active {
  transition: all 0.5s;
}

.list-enter,
.list-leave-to {
  opacity: 0;
  transform: rotateX(90deg);
}

.list-move {
  transition: all 0.5s;
}

.animated-enter-active {
  animation: fadein 1s;
}

.animated-enter-active header {
  display: none;
}

.animated-leave-active header {
  display: none;
}

.animated-leave-active {
  animation: fadeout 1s;
}
@keyframes strikethrough {
  0% {
    width: 0%;
    opacity: 0;
  }
  100% {
    width: 100%;
    opacity: 1;
  }
}
@keyframes fadein {
  0% {
    transform: translateX(-100%);
    opacity: 0;
  }
  50% {
    transform: translateX(0);
  }
  100% {
    opacity: 1;
  }
}

@keyframes fadeout {
  0% {
  }
  50% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(100%);
  }
}

@keyframes okpulse {
  0% {
    background-color: #0c0;
    box-shadow: 0px 0px 36px 0px #0c0;
  }
  50% {
    background-color: #0b0;
    box-shadow: 0px 0px 36px -4px #0b0;
  }
  100% {
    background-color: #0c0;
    box-shadow: 0px 0px 36px 0px #0c0;
  }
}

@keyframes warningpulse {
  0% {
    background-color: #f80;
    box-shadow: 0px 0px 36px 0px #f80;
  }
  50% {
    background-color: #c60;
    box-shadow: 0px 0px 36px -4px #f80;
  }
  100% {
    background-color: #f80;
    box-shadow: 0px 0px 36px 0px #f80;
  }
}

@keyframes errorpulse {
  0% {
    background-color: #f00;
    box-shadow: 0px 0px 36px 0px #ff0000;
  }
  50% {
    background-color: #c00;
    box-shadow: 0px 0px 36px -4px #ff0000;
  }
  100% {
    background-color: #f00;
    box-shadow: 0px 0px 36px 0px #ff0000;
  }
}

@keyframes okpulsefg {
  0% {
    color: #0c0;
    -webkit-text-stroke: 1px black;
    /*box-shadow: 0px 0px 36px 0px #F80;*/
    /*seems like a big performance hit using this */
    text-shadow: #080 0px 0px 26px;
  }
  50% {
    color: #080;
    -webkit-text-stroke: 1px black;
    /*box-shadow: 0px 0px 36px -4px #F80;*/
    text-shadow: #080 0px 0px 3px;
  }
  100% {
    color: #0c0;
    -webkit-text-stroke: 1px black;
    /*box-shadow: 0px 0px 36px 0px #F80;*/
    text-shadow: #080 0px 0px 26px;
  }
}

@keyframes warningpulsefg {
  0% {
    color: #f80;
    -webkit-text-stroke: 1px black;
    /*box-shadow: 0px 0px 36px 0px #F80;*/
    /*seems like a big performance hit using this */
    text-shadow: #f80 0px 0px 26px;
  }
  50% {
    color: #c60;
    -webkit-text-stroke: 1px black;
    /*box-shadow: 0px 0px 36px -4px #F80;*/
    text-shadow: #f80 0px 0px 3px;
  }
  100% {
    color: #f80;
    -webkit-text-stroke: 1px black;
    /*box-shadow: 0px 0px 36px 0px #F80;*/
    text-shadow: #f80 0px 0px 26px;
  }
}

@keyframes errorpulsefg {
  0% {
    color: #f00;
    -webkit-text-stroke: 1px black;
    /*box-shadow: 0px 0px 36px 0px #FF0000;*/
    text-shadow: #ff0000 0px 0px 26px;
  }
  50% {
    color: #c00;
    -webkit-text-stroke: 1px black;
    /*box-shadow: 0px 0px 36px -4px #FF0000;*/
    text-shadow: #ff0000 0px 0px 3px;
  }
  100% {
    color: #f00;
    -webkit-text-stroke: 1px black;
    /*box-shadow: 0px 0px 36px 0px #FF0000;*/
    text-shadow: #ff0000 0px 0px 26px;
  }
}
/***************************************************************************************
    Multiselect
***************************************************************************************/
.boon-multiselect {
  border: none;
  margin: 0;
  min-height: auto !important;
  font-size: 16px;
}
.boon-multiselect-inline {
  display: inline-block !important  ;
}

.boon-multiselect-v2.boon-multiselect-v2__internal .multiselect__input {
  line-height: 1.8;
  margin-top: -0.2rem;
}

.boon-multiselect-v2.boon-multiselect-v2__internal .multiselect__tags-wrap {
  top: 0.6rem !important;
  left: initial;
  bottom: 0.6rem;
  overflow: hidden;
}

.boon-multiselect-v2.boon-multiselect-v2__internal .multiselect__placeholder {
  margin-top: -1rem;
}

.boon-multiselect-v2.boon-multiselect-v2__internal.multiselect--active .multiselect__tags-wrap {
  visibility: hidden;
}

.boon-multiselect-v2 .multiselect__select {
  height: 100%;
}

.boon-multiselect-v2 .multiselect__tags {
  padding: 19px 50px 3px 15px;
  min-height: 55px;
  border-color: #ced4da;
}

.boon-multiselect-v2 .multiselect__tags-wrap {
  top: 63px !important;
  left: 0;
}

.boon-multiselect-v2 .multiselect__tag {
  color: #000;
  padding: 0.375rem 0.75rem;
  background-color: #e4e4e4;
  border-color: #e4e4e4;
  border-radius: 50rem;
  line-height: 1.5;
}

.boon-multiselect-v2 .multiselect__tag-icon {
  position: initial;
}
.boon-multiselect-v2 .multiselect__tag-icon::after {
  color: black;
}

.boon-multiselect-v2 .multiselect__tag-icon:hover::after {
  color: gray;
}
.boon-multiselect-v2 .multiselect__tag-icon:hover {
  background-color: transparent;
}
.boon-multiselect-v2 .multiselect__placeholder,
.boon-multiselect-v2 .multiselect__input,
.boon-multiselect-v2 .multiselect__single {
  padding-left: 0;
  line-height: 1.5;
  color: #212529;
  font-size: 1rem;
  font-weight: 400;
  margin-bottom: 0;
}

/* .boon-multiselect-v2 .multiselect__single {
  margin-top: -0.4rem;
} */
.boon-multiselect-v2.boon-multiselect-v2__centered .multiselect__placeholder {
  margin-top: -0.4rem;
}

.boon-multiselect-v2__show-input .multiselect__placeholder,
.boon-multiselect-v2__show-input .multiselect__input {
  width: 80% !important;
}

.boon-multiselect .multiselect__tags {
  border-radius: 0;
  border: 1px solid transparent;
  border-bottom: 1px solid #ccc;
  margin-bottom: 0;
  width: 100%;
  min-height: 40px;
  padding-left: 0;
}

.boon-multiselect .multiselect__input {
  margin-bottom: 0;
  border-radius: 0;
  font-size: 16px;
  font-weight: 400;
  line-height: normal !important;
  padding-left: 0;
}

.boon-multiselect .multiselect__tag,
.boon-multiselect .multiselect__option--highlight,
.boon-multiselect .multiselect__option--highlight:after {
  background: #1c2731;
}

.boon-multiselect .multiselect__tag-icon:after {
  color: white;
}

.boon-multiselect .multiselect__tag-icon:focus,
.boon-multiselect .multiselect__tag-icon:hover {
  background: #1c2731;
}

.boon-input-container {
  margin: 7px 14px;
  width: calc(50% - 28px);
}

/* -new is added to avoid conflict with existing inputs during "migration"   */
.boon-multiselect-new .multiselect__single {
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}
/***************************************************************************************
    Form items
***************************************************************************************/
.boonCheckboxWrapper input[type='checkbox'] {
  opacity: 0;
  position: absolute;
}

.boonCheckboxWrapper input[type='checkbox'] label {
  position: relative;
  display: inline-block;
  vertical-align: middle;
  margin: 5px;
  cursor: pointer;
}

.boonCheckboxWrapper input[type='checkbox'] + label:before {
  content: '';
  background: #fff;
  border: 1px solid #1c2731;
  display: inline-block;
  vertical-align: middle;
  width: 15px;
  height: 15px;
  padding: 1px;
  margin-right: 10px;
  text-align: center;
  border-radius: 50%;
}

.boonCheckboxWrapper input[type='checkbox']:checked + label:before {
  background: #1c2731;
  box-shadow: inset 0px 0px 0px 3px #fff;
}

.boonCheckboxWrapper input[type='checkbox']:focus + .boonCheckboxWrapper input[type='checkbox'] label {
  outline: 1px solid #1c2731;
}

.pure-material-button-contained {
  position: relative;
  box-sizing: border-box;
  border: none;
  box-shadow: 0 3px 1px -2px rgba(0, 0, 0, 0.2), 0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 1px 5px 0 rgba(0, 0, 0, 0.12);
  overflow: hidden;
  cursor: pointer;
  transition: box-shadow 0.2s;
}

/* Overlay */
.pure-material-button-contained::before {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: rgb(var(--pure-material-onprimary-rgb, 255, 255, 255));
  opacity: 0;
  transition: opacity 0.2s;
}

/* Ripple */
.pure-material-button-contained::after {
  content: '';
  position: absolute;
  left: 50%;
  top: 50%;
  border-radius: 50%;
  padding: 50%;
  width: 32px; /* Safari */
  height: 32px; /* Safari */
  background-color: rgb(var(--pure-material-onprimary-rgb, 255, 255, 255));
  opacity: 0;
  transform: translate(-50%, -50%) scale(1);
  transition: opacity 1s, transform 0.5s;
}

/* Hover, Focus */
.pure-material-button-contained:hover,
.pure-material-button-contained:focus {
  box-shadow: 0 2px 4px -1px rgba(0, 0, 0, 0.2), 0 4px 5px 0 rgba(0, 0, 0, 0.14), 0 1px 10px 0 rgba(0, 0, 0, 0.12);
}

.pure-material-button-contained:hover::before {
  opacity: 0.08;
}

.pure-material-button-contained:focus::before {
  opacity: 0.24;
}

.pure-material-button-contained:hover:focus::before {
  opacity: 0.3;
}

/* Active */
.pure-material-button-contained:active {
  box-shadow: 0 5px 5px -3px rgba(0, 0, 0, 0.2), 0 8px 10px 1px rgba(0, 0, 0, 0.14), 0 3px 14px 2px rgba(0, 0, 0, 0.12);
}

.pure-material-button-contained:active::after {
  opacity: 0.32;
  transform: translate(-50%, -50%) scale(0);
  transition: transform 0s;
}

/***************************************************************************************
    Scrollbar
***************************************************************************************/
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}
::-webkit-scrollbar-button {
  width: 0px;
  height: 0px;
}
::-webkit-scrollbar-thumb {
  background: white;
  border: 1px solid #ddd;
  border-radius: 50px;
}
::-webkit-scrollbar-thumb:hover {
  background: #ffffff;
}
::-webkit-scrollbar-thumb:active {
  background: #000000;
}
::-webkit-scrollbar-track {
  background: #666666;
  border: 1px solid #ffffff;
  border-radius: 50px;
}
::-webkit-scrollbar-track:hover {
  background: #666666;
}
::-webkit-scrollbar-track:active {
  background: #333333;
}
::-webkit-scrollbar-corner {
  background: transparent;
}

/***************************************************************************************
    Checkbox
***************************************************************************************/
.customCheckbox {
  opacity: 0;
}

.customCheckbox + label {
  margin: 0 0 0 20px;
  position: relative;
  cursor: pointer;
  font-size: 16px;
}

.customCheckbox + label ~ label {
  margin: 0 0 0 40px;
}

.customCheckbox + label::before {
  content: ' ';
  position: absolute;
  left: -35px;
  top: -3px;
  width: 25px;
  height: 25px;
  display: block;
  background: white;
  border: 1px solid #a9a9a9;
}

.customCheckbox + label::after {
  content: ' ';
  position: absolute;
  left: -35px;
  top: -3px;
  width: 23px;
  height: 23px;
  display: block;
  z-index: 1;
  background: url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjE4MS4yIDI3MyAxNyAxNiIgZW5hYmxlLWJhY2tncm91bmQ9Im5ldyAxODEuMiAyNzMgMTcgMTYiPjxwYXRoIGQ9Ik0tMzA2LjMgNTEuMmwtMTEzLTExM2MtOC42LTguNi0yNC04LjYtMzQuMyAwbC01MDYuOSA1MDYuOS0yMTIuNC0yMTIuNGMtOC42LTguNi0yNC04LjYtMzQuMyAwbC0xMTMgMTEzYy04LjYgOC42LTguNiAyNCAwIDM0LjNsMjMxLjIgMjMxLjIgMTEzIDExM2M4LjYgOC42IDI0IDguNiAzNC4zIDBsMTEzLTExMyA1MjQtNTI0YzctMTAuMyA3LTI1LjctMS42LTM2eiIvPjxwYXRoIGZpbGw9IiMzNzM3MzciIGQ9Ik0xOTcuNiAyNzcuMmwtMS42LTEuNmMtLjEtLjEtLjMtLjEtLjUgMGwtNy40IDcuNC0zLjEtMy4xYy0uMS0uMS0uMy0uMS0uNSAwbC0xLjYgMS42Yy0uMS4xLS4xLjMgMCAuNWwzLjMgMy4zIDEuNiAxLjZjLjEuMS4zLjEuNSAwbDEuNi0xLjYgNy42LTcuNmMuMy0uMS4zLS4zLjEtLjV6Ii8+PHBhdGggZD0iTTExODcuMSAxNDMuN2wtNTYuNS01Ni41Yy01LjEtNS4xLTEyLTUuMS0xNy4xIDBsLTI1My41IDI1My41LTEwNi4yLTEwNi4yYy01LjEtNS4xLTEyLTUuMS0xNy4xIDBsLTU2LjUgNTYuNWMtNS4xIDUuMS01LjEgMTIgMCAxNy4xbDExNC43IDExNC43IDU2LjUgNTYuNWM1LjEgNS4xIDEyIDUuMSAxNy4xIDBsNTYuNS01Ni41IDI2Mi0yNjJjNS4yLTMuNCA1LjItMTIgLjEtMTcuMXpNMTYzNC4xIDE2OS40bC0zNy43LTM3LjdjLTMuNC0zLjQtOC42LTMuNC0xMiAwbC0xNjkuNSAxNjkuNS03MC4yLTcxLjljLTMuNC0zLjQtOC42LTMuNC0xMiAwbC0zNy43IDM3LjdjLTMuNCAzLjQtMy40IDguNiAwIDEybDc3LjEgNzcuMSAzNy43IDM3LjdjMy40IDMuNCA4LjYgMy40IDEyIDBsMzcuNy0zNy43IDE3NC43LTE3Ni40YzEuNi0xLjcgMS42LTYuOS0uMS0xMC4zeiIvPjwvc3ZnPg==')
    no-repeat center center;
  transition: all 0.3s ease;
  transform: scale(0);
  opacity: 0;
}

.customCheckbox:checked + label::after {
  transform: scale(1);
  opacity: 1;
}
/***************************************************************************************
    Tooltip
***************************************************************************************/
.tooltip h1 {
  font-size: 110%;
  margin-top: 0.5em;
}

.tooltip {
  display: block !important;
  z-index: 10000;
  font-size: 1em;
  font-weight: 400;
  max-width: 25vw;
}

.tooltip .tooltip-inner {
  background: transparent;
  color: white;
  border-radius: 0;
  padding: 5px 10px 4px;
}

.tooltip .tooltip-arrow {
  width: 0;
  height: 0;
  border-style: solid;
  position: absolute;
  margin: 5px;
  border-color: black;
  z-index: 1;
}

.tooltip[x-placement^='top'] {
  margin-bottom: 5px;
}

.tooltip[x-placement^='top'] .tooltip-arrow {
  border-width: 5px 5px 0 5px;
  border-left-color: transparent !important;
  border-right-color: transparent !important;
  border-bottom-color: transparent !important;
  bottom: -5px;
  left: calc(40% - 5px);
  margin-top: 0;
  margin-bottom: 0;
}

.tooltip[x-placement^='bottom'] {
  margin-top: 5px;
}

.tooltip[x-placement^='bottom'] .tooltip-arrow {
  border-width: 0 5px 5px 5px;
  border-left-color: transparent !important;
  border-right-color: transparent !important;
  border-top-color: transparent !important;
  top: -5px;
  left: calc(50% - 5px);
  margin-top: 0;
  margin-bottom: 0;
}

.tooltip[x-placement^='right'] {
  margin-left: 5px;
}

.tooltip[x-placement^='right'] .tooltip-arrow {
  border-width: 5px 5px 5px 0;
  border-left-color: transparent !important;
  border-top-color: transparent !important;
  border-bottom-color: transparent !important;
  left: -5px;
  top: calc(50% - 5px);
  margin-left: 0;
  margin-right: 0;
}

.tooltip[x-placement^='left'] {
  margin-right: 5px;
}

.tooltip[x-placement^='left'] .tooltip-arrow {
  border-width: 5px 0 5px 5px;
  border-top-color: transparent !important;
  border-right-color: transparent !important;
  border-bottom-color: transparent !important;
  right: -5px;
  top: calc(50% - 5px);
  margin-left: 0;
  margin-right: 0;
}

.tooltip.popover .popover-inner {
  background: #f9f9f9;
  color: black;
  padding: 24px;
  border-radius: 5px;
  box-shadow: 0 5px 30px rgba(black, 0.1);
}

.tooltip.popover .popover-arrow {
  border-color: #f9f9f9;
}

.tooltip[aria-hidden='true'] {
  visibility: hidden;
  opacity: 0;
  transition: opacity 0.15s, visibility 0.15s;
}

.tooltip[aria-hidden='false'] {
  visibility: visible;
  opacity: 1;
  transition: opacity 0.15s;
}
.switch {
  width: 60px;
  height: 35px;
  position: relative;
}
.switch label {
  width: 100%;
  height: 100%;
  position: relative;
  display: block;
}

.switch input {
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  opacity: 0;
  z-index: 100;
  position: absolute;
  width: 100%;
  height: 100%;
  cursor: pointer;
}

.switch label {
  display: block;
  width: 100%;
  height: 100%;
  background: #848282;
  border-radius: 25px;
}

.switch label:after {
  content: '';
  position: absolute;
  z-index: -1;
  top: -8px;
  right: -8px;
  bottom: -8px;
  left: -8px;
  border-radius: inherit;
}

.switch label:before {
  content: '';
  position: absolute;
  z-index: -1;
  top: -18px;
  right: -18px;
  bottom: -18px;
  left: -18px;
}

.switch label i {
  display: block;
  height: 55px;
  width: 70%;
  border-radius: inherit;
  position: absolute;
  z-index: 2;
  right: 59%;
  top: -5px;
  border-radius: 100%;
}

.switch label i:after {
  content: '';
  position: absolute;
  left: 51%;
  top: 15%;
  width: 60%;
  height: 50%;
  background: #fff;
  border-radius: inherit;
}

.switch input:checked ~ label {
  background: #65bd62;
}

.switch input:checked ~ label i {
  right: 15%;
}
.v--modal-overlay .diaryModal {
  border-radius: 25px;
  background: #e0e1e3;
  height: 470px !important;
  padding: 0;
  display: flex;
  align-items: center;
}

.links li {
  font-weight: 200;
}

.links li img {
  width: 34px;
  margin-right: 15px;
  height: 34px;
}
aside {
  height: 100vh;
}

.v--modal-overlay .assignModal {
  border-radius: 25px;
  background: #e0e1e3;
}

.assignModal .multiselect__content {
  position: relative;
}
.assignModal .multiselect__content li {
  display: block;
  width: 100%;
  text-align: left;
}
.assignModal .labelled .title {
  text-align: left;
  color: #051623;
  font-family: Montserrat;
  font-weight: 500;
  font-size: 18px;
  margin-bottom: 3px;
}
.assignModal .labelled p {
  color: #000;
  font-weight: 500;
  font-size: 12px;
  text-align: left;
  margin-bottom: 5px;
}
.assignModal .multiselect__content-wrapper {
  max-height: 200px !important;
}
.assignModal .next-btn {
  margin-left: auto;
  margin-right: auto;
  width: 50px;
  background: green;
  background-image: linear-gradient(to right, #7bb5a7, #56bd87);
  height: 50px;
  text-transform: uppercase;
  border-radius: 100%;
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
}
.assignModal .boon-multiselect .multiselect__tags {
  border-radius: 25px;
  border-bottom: 0;
  padding-left: 5px;
}
.assignModal .multiselect__single {
  font-size: 12px;
  font-weight: 500;
}

.person-edit .boon-multiselect .multiselect__tag {
  padding: 5px 26px 5px 10px;
  border-radius: 0;
}
.person-edit .boon-multiselect .multiselect__tag span {
  font-weight: 500 !important;
}
.person-edit .boon-multiselect .multiselect__single {
  font-weight: 500 !important;
  font-size: 0.875rem;
  font-family: PoppinsRegular, sans-serif;
}
.person-edit #project-list .boon-multiselect .multiselect__placeholder {
  display: none;
}
.person-edit .boon-input {
  font-weight: 700;
  font-size: 18px;
  font-family: 'DIN Alternate Bold';
}
.person-edit #project-list .multiselect {
  margin-top: 5px;
}
.person-edit .multiselect {
  font-size: 1rem;
}
.person-edit #project-list .boon-multiselect .multiselect__tags {
  border: 1px solid #ccc;
}
.person-edit .multiselect__select:before {
  border-color: #000 transparent transparent;
}
.person-edit .multiselect__tags-wrap {
  display: inline;
  position: absolute;
  top: 55px;
}
.person-edit .inputContainer .boon-multiselect-label--hidden {
  color: #101010;
  /* position: absolute;
    top: 15px;
    left: 0;
    z-index: 1; */
}
.person-edit .boon-input:focus ~ label,
.person-edit .boon-input-filled ~ label {
  font-family: 'DIN-Light' !important;
  color: #051623 !important;
}
.person-edit #authorization .title {
  font-family: 'DIN-Light' !important;
  color: #051623 !important;
}
.EditProject .boon-input {
  border: 1px solid #bbb6bb55 !important;
  border-radius: 5px;
  padding: 7px 5px !important;
  font-size: 26px;
  font-family: 'DIN Alternate Bold';
}
.EditProject .boon-input-filled ~ label {
  top: -20px !important;
  font-size: 12px !important;
}
.EditProject .boon-input ~ label {
  top: -20px !important;
  font-size: 14px !important;
  color: #fff !important;
  font-family: 'DIN-Light';
}
.EditProject .focus-border {
  display: none !important;
}
.EditProject .boon-input-container.free-text input {
  height: 200px;
}
.EditProject .dropDown-list li .boon-input-container {
  width: calc(100% - 28px);
  margin-bottom: 0;
}
.diary-toggle input[type='checkbox']:checked + label:before {
  background: red;
}
.diary-toggle input[type='checkbox'] + label:before {
  border: 1px solid red;
}
.EditProject .boon-multiselect .multiselect__tags {
  background: #242f39;
  border: 1px solid #bbb6bb55 !important;
  border-radius: 5px;
  min-height: 44px;
  padding: 0;
}
.EditProject .multiselect__input {
  padding-left: 7px;
}
.EditProject .multiselect__input,
.EditProject .multiselect__single {
  background: transparent;
  color: white;
  font-family: 'DIN Alternate Bold';
  font-size: 26px;
  min-height: 44px;
  line-height: 44px;
  padding-left: 5px;
  margin-bottom: 0;
}
.EditProject .multiselect__input:focus,
.EditProject .multiselect__single:focus {
  border: 0 !important;
}
.EditProject .multiselect__content-wrapper {
  background: #242f39;
  color: white;
  border: 0;
}
.EditProject .multiselect__option {
  margin-left: 0px;
  padding-left: 0px;
  font-size: 18px;
  color: #9c9c9c;
  padding: 10px;
  min-height: 31px;
  line-height: 10px;
}
.EditProject .multiselect__option--selected {
  background: #14293c;
  color: white;
  font-weight: 200;
  font-family: 'DIN-Light';
}
.EditProject .boon-multiselect .multiselect__tag,
.EditProject .boon-multiselect .multiselect__option--highlight,
.EditProject .boon-multiselect .multiselect__option--highlight:after {
  background: transparent;
  text-decoration: underline;
  color: white;
  font-family: 'DIN-Light';
}
.EditProject .boon-multiselect .multiselect__option--highlight:after {
  visibility: hidden;
}
.EditProject .multiselect--disabled .multiselect__current,
.EditProject .multiselect--disabled .multiselect__select,
.EditProject .multiselect__option--disabled {
  background: transparent;
  display: none;
}
.EditProject .multiselect__option:after {
  line-height: 30px;
}
.EditProject .ProjectUserListItem input.new-add {
  background: transparent;
  border: 0 !important;
  font-size: 12px;
  color: #fff;
  font-weight: 200;
  padding: 0 !important;
  width: 250px;
}
.EditProject .boon-multiselect .multiselect__placeholder {
  display: inline-block;
  padding-top: 0px;
  background: transparent;
  color: white;
  font-size: 22px;
  font-weight: 500;
  padding-left: 5px;
  margin-bottom: 0;
  line-height: 42px;
}
.EditProject .dropDown-list .boon-input {
  margin-left: -15px;
  font-size: 14px !important;
  font-weight: 200;
  width: 102%;
  /* font-family: "DIN-Light"; */
  height: 35px;
}
.pac-logo:after {
  display: none !important;
}
.EditProject .boon-multiselect.NewOccupation {
  width: 70%;
}
.EditProject .boon-multiselect.NewOccupation .multiselect__tags {
  background: #2c3842;
  border: 1px solid #69626955 !important;
  border-radius: 5px;
}
.ProjectLogg .switch {
  width: 40px;
  height: 19px;
  position: relative;
}
.ProjectLogg .switch label {
  background: #3f414c9e;
}
.ProjectLogg .switch label i {
  height: 30px;
  width: 60%;
  right: 64%;
  top: -2px;
}
.ProjectLogg .switch input:checked ~ label {
  background: #1bb698;
}
.ProjectLogg .switch label i:after {
  background: #091d2f;
}
.EditProject .v--modal-overlay {
  background-color: rgba(31, 28, 28, 0.9);
  opacity: 0.7;
}
.EditProject .vdp-datepicker__calendar {
  position: absolute;
  z-index: 100;
  background: #2c3842;
  width: auto;
  border: 1px solid #bbb6bb55 !important;
  border-radius: 5px;
}
.EditProject .datepicker {
  margin-top: -15px;
}
.EditProject .datepicker .vdp-datepicker input {
  width: 100%;
  height: 46px;
  border: 1px solid #bbb6bb55 !important;
  border-radius: 5px;
  padding-left: 0;
  padding-right: 0;
  background: transparent;
  color: #fff;
  font-size: 26px;
  font-family: 'DIN Alternate Bold';
  padding: 7px 5px !important;
  margin-top: 2px;
}
.vdp-datepicker__calendar header .prev:not(.disabled):hover,
.vdp-datepicker__calendar header .next:not(.disabled):hover,
.vdp-datepicker__calendar header .up:not(.disabled):hover {
  background: transparent !important;
}
.company-edit .boon-input:focus ~ label,
.company-edit .boon-input-filled ~ label {
  font-family: 'DIN-Light' !important;
  color: #051623 !important;
  top: -12px !important;
}
.company-edit .boon-input {
  font-weight: 700;
  font-size: 18px;
  font-family: 'DIN Alternate Bold';
}

.company-edit .boon-multiselect-label {
  font-family: 'DIN-Light' !important;
  color: #051623 !important;
}
.tooltip-bubble {
  background: #1c2731;
  padding: 15px;
  border-radius: 10px;
}
.tooltip-bubble strong {
  background-color: rgb(60, 94, 100);
  font-weight: bold;
}

.tooltip-bubble .tooltip-arrow {
  border-color: #1c2731;
}

.EditProject .inputContainer.multiselect .boon-multiselect-label {
  top: -20px !important;
  font-size: 14px !important;
  color: #fff;
  font-family: 'DIN-Light';
}
.EditProject .inputContainer.multiselect {
  margin-top: -14px;
}
.EditProject .inputContainer.multiselect .multiselect__tags {
  background: #242f39;
  border: 1px solid #bbb6bb55 !important;
  border-radius: 5px;
  min-height: 47px;
}
@media (max-width: 767px) {
  header {
    background: #1c2639;
  }
  body {
    background: #1c2639;
  }
  main {
    margin-top: 0;
  }
  .overview-icon .material-design-icon {
    height: auto;
    width: auto;
  }
  .overview-icon .material-design-icon > .material-design-icon__svg {
    color: #a0aaba;
    font-size: 22px;
    position: unset;
    height: auto;
    width: auto;
    /* margin-top: 4px; */
  }
  .v--modal-overlay .diaryModal {
    top: 100px !important;
  }
}

.position-relative {
  position: relative !important;
}

.modal-dialog.modal-color-black {
  color: black;
}

.modal-dialog.modal-90 {
  min-width: 90%;
}

.modal-dialog.modal-70 {
  min-width: 70%;
}

.modal-dialog.modal-no-padding .modal-body {
  padding: 0;
}
</style>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style src="@/styles/typography.css"></style>
<style src="@/styles/bootstrap-override.css"></style>



// WEBPACK FOOTER //
// src/App.vue